// Uygulama geneli kimlik dogrulama ve kullanici/arac izni state'i.
//
// Amac: /users/me ve /tool-permissions/me cagrilarinin sayfadan sayfaya
// tekrar etmesinin onune gecmek. Bir sayfa ziyareti, kac tane "/users/me"
// cagrisi gorurse gorsun sadece bir kere istek yapilir.
//
// Kullanim:
//   const { user, toolPermissions, loading, refresh, logout } = useAuth();
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiClient } from "../services/apiClient";
import { clearLegacyAuthStorage } from "../utils/authStorage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [toolPermissions, setToolPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Uygulama acilirken eski surumlerden kalan localStorage auth
    // kayitlarini bir kere temizle.
    clearLegacyAuthStorage();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const [userRes, permRes] = await Promise.all([
        apiClient.get("/users/me"),
        apiClient
          .get("/tool-permissions/me")
          .catch(() => ({ data: { permissions: [] } })),
      ]);
      if (!mountedRef.current) return;
      setUser(userRes.data);
      setToolPermissions(permRes.data?.permissions || []);
      setError("");
    } catch (err) {
      if (!mountedRef.current) return;
      setUser(null);
      setToolPermissions([]);
      setError(err?.message || "auth_failed");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/users/logout");
    } catch {
      // ignore -- sunucu 401 donerse bile client state temizlenmeli
    }
    clearLegacyAuthStorage();
    if (mountedRef.current) {
      setUser(null);
      setToolPermissions([]);
    }
  }, []);

  const value = useMemo(
    () => ({ user, toolPermissions, loading, error, refresh: load, logout }),
    [user, toolPermissions, loading, error, load, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
